/** Zero-pads a month value to two digits. Accepts a number or numeric string. */
export const normalizeMonth = month => String(month).padStart(2, '0');

/**
 * Converts a display string to PascalCase and strips non-alphanumeric characters.
 *
 * Examples:
 *   sanitizeName('Customer Aging by Project') → 'CustomerAgingByProject'
 *   sanitizeName('Acme Portfolio & Solar')    → 'AcmePortfolioSolar'
 */
export function sanitizeName(str) {
  return str
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
    .replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * Generates a UTC-timestamped filename stem (no extension).
 *
 * Format: ReportName_YYYY-MM-DD_HHMM  (default)
 *         ReportName_YYYY-MM_HHMM     (when period is provided)
 *
 * @param {string} baseName         - Sanitized filename stem. May be a single PascalCase
 *                                    word ('BulkEdit') or an underscore-delimited sequence
 *                                    of PascalCase segments
 *                                    ('ExpectedProjectRevenue_AcmePortfolio_ByProject').
 *                                    Underscores are valid segment separators — do not
 *                                    strip them before passing.
 * @param {object} [options]
 * @param {string} [options.period] - Billing period in YYYY-MM format; replaces the full date.
 *                                    Must match /^\d{4}-\d{2}$/ — invalid values fall back
 *                                    to the full UTC date so filenames remain well-formed.
 *
 * Examples:
 *   generateFileName('BulkEdit')                              → 'BulkEdit_2026-04-16_0914'
 *   generateFileName('StatementStatus', { period: '2026-01' }) → 'StatementStatus_2026-01_0914'
 *   generateFileName('StatementStatus', { period: '2026-1' })  → 'StatementStatus_2026-04-16_0914' (fallback)
 */
const PERIOD_RE = /^\d{4}-\d{2}$/;

export function generateFileName(baseName, { period } = {}) {
  const now = new Date();
  const hhmm = now
    .toISOString()
    .slice(11, 16)
    .replace(':', '');
  const validPeriod = period && PERIOD_RE.test(period) ? period : null;
  const dateSegment = validPeriod ?? now.toISOString().slice(0, 10);
  return `${baseName}_${dateSegment}_${hhmm}`;
}
